import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Toast, { ToastProps } from '../components/Toast';

interface ToastContextType {
  showToast: (props: Omit<ToastProps, 'id' | 'onClose'>) => void;
  success: (title: string, description?: string, duration?: number) => void;
  error: (title: string, description?: string, duration?: number) => void;
  warning: (title: string, description?: string, duration?: number) => void;
  info: (title: string, description?: string, duration?: number) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'top-right',
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const location = useLocation();

  // Clear all toasts when route changes
  useEffect(() => {
    setToasts([]);
  }, [location.pathname]);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const addToast = (props: Omit<ToastProps, 'id' | 'onClose'>) => {
    const id = generateId();
    const newToast: ToastProps = {
      ...props,
      id,
      onClose: (toastId) => removeToast(toastId),
    };

    setToasts((prev) => {
      const updated = [...prev, newToast];
      // Keep only the latest maxToasts
      return updated.slice(-maxToasts);
    });
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showToast = (props: Omit<ToastProps, 'id' | 'onClose'>) => {
    addToast(props);
  };

  const success = (title: string, description?: string, duration?: number) => {
    addToast({ type: 'success', title, description, duration });
  };

  const error = (title: string, description?: string, duration?: number) => {
    addToast({ type: 'error', title, description, duration });
  };

  const warning = (title: string, description?: string, duration?: number) => {
    addToast({ type: 'warning', title, description, duration });
  };

  const info = (title: string, description?: string, duration?: number) => {
    addToast({ type: 'info', title, description, duration });
  };

  const clearToasts = () => {
    setToasts([]);
  };

  const getPositionStyles = () => {
    const baseStyles = "fixed z-50 flex flex-col gap-2 p-4 pointer-events-none";
    
    switch (position) {
      case 'top-right':
        return `${baseStyles} top-0 right-0`;
      case 'top-left':
        return `${baseStyles} top-0 left-0`;
      case 'bottom-right':
        return `${baseStyles} bottom-0 right-0`;
      case 'bottom-left':
        return `${baseStyles} bottom-0 left-0`;
      default:
        return `${baseStyles} top-0 right-0`;
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info, clearToasts }}>
      {children}
      
      {/* Toast Container */}
      <div className={getPositionStyles()}>
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast {...toast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
